import {
  EXPERIENCE_TYPE_GM, BUNDLE_CONFIGURATION,
  PRODUCTS_CONFIGURATION, BUNDLE_TYPE_COMBO,
  OFFERING_CONFIGS,
  PRICE_TYPES
} from '../constants/index';
import {
  hasArrayValues,
  getAreAnyProductsOOS,
  getProductsFulfillmentInventories,
  getStateNameStateCode, getExcludedStatus,
  getAreFulfillmentOptionsAvailable
} from './product-bundle-utils';

import { getVariantName, getSelectableVariants } from './selectable-variant-utils';
import {
  isProductDiscontinued, getExperienceType, getBundleType, isHiddenItem, isObsoleteItem
} from './product-unavailable-utils';

export const getBrandName = (product, products) => {
  const { brand } = product?.bundleSpecificationDetails ?? {};
  if (brand) {
    return brand;
  }

  const { brandName: productBrandName } = product?.identifiers ?? {};
  if (productBrandName) {
    return productBrandName;
  }

  const { brandName: bundleBrandName } = products?.[0]?.identifiers ?? {};
  const showBrandName = products?.every((bundleProduct) =>
    bundleBrandName && bundleProduct?.identifiers?.brandName === bundleBrandName
  );
  return showBrandName ? bundleBrandName : null;
};

const getBrandUrl = (product) => {
  const brandLinkUrl = product?.taxonomy?.brandLinkUrl;
  if (brandLinkUrl) {
    return brandLinkUrl;
  }
  const lastBreadCrumb = product?.taxonomy?.breadCrumbs?.length - 1;
  return product?.taxonomy?.breadCrumbs?.[lastBreadCrumb]?.url || null;
};

export const getProductFeatures = (bundleType, experienceType) => {
  if (!experienceType || !bundleType) {
    return {};
  }

  return BUNDLE_CONFIGURATION?.[bundleType]?.[experienceType] || {};
};

let staticSelectableVariants = {};
const getProductComponent = (bundleProduct, bundleProducts, experienceType) => {
  const updatedProductComponents = bundleProduct?.bundleSpecificationDetails?.components?.map((component, index) => {

    const id = component?.defaultProductId;
    if (!staticSelectableVariants[id] && bundleProducts.length > 0) {
      const variantName = getVariantName(experienceType, bundleProducts[index]);
      if (variantName) {
        staticSelectableVariants[id] = {
          [variantName]: getSelectableVariants(
            component?.selectableVariants,
            variantName
          )
        };
      }
    }

    return {
      defaultProductId: component?.defaultProductId,
      allowSwap: component?.allowSwap,
      allowRemove: component?.allowRemove,
      quantity: component?.quantity,
      priority: component?.priority,
      selectableVariants: staticSelectableVariants[id]
    };
  });

  const sortedProductComponents = updatedProductComponents
    ?.sort((product1, product2) => product1.priority - product2.priority);

  return sortedProductComponents;
};

export const getUpdatedProduct = (bundleProduct, bundleProducts, configExperienceType, configBundleType) => {
  if (!bundleProduct || Object.keys(bundleProduct).length === 0) {
    return null;
  }
  const experienceType = configExperienceType || getExperienceType(bundleProduct);
  const bundleType = configBundleType || getBundleType(bundleProduct);
  return {
    ...bundleProduct,
    features: {
      brand: getBrandName(bundleProduct, bundleProducts),
      brandUrl: getBrandUrl(bundleProduct),
      ...getProductFeatures(bundleType, experienceType)
    },
    productComponent: getProductComponent(bundleProduct, bundleProducts, experienceType)
  };
};

const getSwapAndRemoveFlag = (filteredProduct) => {
  if (!filteredProduct) {
    return {};
  }

  const { allowSwap, allowRemove } = filteredProduct || {};
  const swapRemoveObject = { allowSwap: !!allowSwap, allowRemove: !!allowRemove };
  return swapRemoveObject;
};

export const getProductsFeature = (filteredProduct, bundleType, experienceType) => {
  if (!bundleType || !experienceType) {
    return {};
  }

  const productConfiguration = PRODUCTS_CONFIGURATION?.[bundleType]?.[experienceType];

  if (!productConfiguration) {
    return {};
  }

  const swapAndRemoveFlag = getSwapAndRemoveFlag(filteredProduct);

  return {
    ...productConfiguration,
    ...swapAndRemoveFlag
  };
};

export const isMissingProducts = (bundleProductIds, bundleProducts, selectableVariantsItemIds, accessoryItemIds) => {
  const mainBundleProducts = bundleProducts?.filter?.((product) =>
    !selectableVariantsItemIds?.some((id) => product?.itemId === id)
    && !accessoryItemIds?.some((id) => product?.itemId === id));
  return bundleProductIds?.length !== mainBundleProducts?.length;
};

export const getProductPricing = ({ product, type = PRICE_TYPES.SIMPLE } = {}) => {
  if (!product) return null;

  if (type === PRICE_TYPES.SIMPLE) {
    return product?.pricing;
  }

  const { original, value, promotion } = product?.pricing || {};

  // Structure based on Minimal Price Component
  // eslint-disable-next-line max-len
  // https://github.com/one-thd/ocm_pd_component_price/blob/master/docs/chapters/priceMinimal/examples/bulkStrikethrough/mock.js

  return {
    ...product?.pricing,
    cartItem: {
      total: value * (product?.quantity || 1)
    },
    original: (original || value),
    value,
    promotion: {
      ...(promotion || {}),
      message: 'Save '
    }
  };
};

export const getProductAvailabilityData = (product, checkAvailabilityData) => {
  const availabilityInfoList = checkAvailabilityData?.checkAvailability?.fulfillmentOptions?.[0]?.services?.[0]
    ?.deliveryAvailabilityDetail || [];
  const productAvailability = availabilityInfoList.find((info) => info.itemId === product?.itemId);

  if (productAvailability) {
    return productAvailability;
  }

  return null;
};

export const getExcludedShipState = ({ bundleProductItem, deliveryZip, storeZip }) => {

  const productsFulfillmentInventories = getProductsFulfillmentInventories({
    products: [bundleProductItem]
  });

  const {
    deliveryStateName = '',
    deliveryStateCode = '',
    storeStateName = '',
    storeStateCode = ''
  } = getStateNameStateCode({ deliveryZip, storeZip });

  const excludedStatus = productsFulfillmentInventories?.reduce((excludeStateAccumulator, product) => {
    if (excludeStateAccumulator?.isExcluded) {
      return excludeStateAccumulator;
    }

    const bossExcluded = getExcludedStatus({ product, storeStateCode });
    const shipExcluded = getExcludedStatus({ product, deliveryStateCode });
    const quantity = product?.quantity || 1;
    const isBopisNotFulfillableOOS = product?.inventory?.BOPIS < quantity;
    if (bossExcluded?.status && shipExcluded?.status && isBopisNotFulfillableOOS) {
      return { ...excludeStateAccumulator, isExcluded: true };
    }

    return excludeStateAccumulator;
  },
  { isExcluded: false, deliveryStateName, storeStateName, storeStateCode });

  return excludedStatus;
};

export const isProductOOS = ({
  product, experienceType, bundleType, checkAvailabilityData, isExcludedShipState
}) => {
  // If the product is Laundry or Kitchen (non-GM) combo bundle we check Availability data
  if (!product || !(experienceType === EXPERIENCE_TYPE_GM && bundleType === BUNDLE_TYPE_COMBO)) {
    if (!checkAvailabilityData) {
      return false;
    }

    const availability = getProductAvailabilityData(product, checkAvailabilityData);

    if (!availability) {
      return false;
    }

    return (
      availability.status.includes('OOS_')
      && !isHiddenItem({ product })
      && !isProductDiscontinued({ product, checkAvailabilityData })
      && !isObsoleteItem({ product })
    );
  }
  const { backordered } = product?.fulfillment || {};
  const isOOSProduct = getAreAnyProductsOOS({ products: [product] });
  const productInventories = getProductsFulfillmentInventories({ products: [product] });
  const isFulfillmentAvailable = getAreFulfillmentOptionsAvailable({ productInventories });
  return (isOOSProduct || backordered || !isFulfillmentAvailable)
    && !isHiddenItem({ product })
    && !isProductDiscontinued({ product, checkAvailabilityData }) && !isObsoleteItem({ product })
    && !isExcludedShipState;
};

const isSelectableVariantProduct = ({ product, selectableVariantsItemIds }) => {
  return selectableVariantsItemIds?.some((variantId) => variantId === product?.itemId);
};

const isAccessoryProduct = ({ product, accessoryItemIds }) => {
  return accessoryItemIds?.some((accessory) => accessory === product?.itemId);
};

// temp solution until we get a final solution to identify promo image at item level
export const getUpdatedMedia = ({ product, removePromoImage }) => {
  if (!removePromoImage) return product?.media;

  if (!product?.media?.images || !Array.isArray(product?.media?.images) || product?.media?.images?.length < 2) {
    return product?.media;
  }

  const indexPromoImage = product.media?.images?.findIndex((image) => image.subType === 'PRIMARY');
  const indexCatalogImage = product.media?.images?.findIndex((image) => image.type === 'IMAGE_CATALOG_VIEW');

  if (indexPromoImage > -1 && indexCatalogImage > -1) {
    const newImages = [...product.media.images];
    newImages[indexPromoImage] = {
      ...product.media.images[indexPromoImage],
      url: product.media.images[indexCatalogImage].url,
      sizes: product.media.images[indexCatalogImage].sizes
    };

    newImages.splice(indexCatalogImage, 1);
    return {
      ...product.media,
      images: newImages
    };
  }

  return product?.media;
};

export const getUpdatedProducts = (
  bundleProduct, bundleProducts, bundleProductIds, configExperienceType, configBundleType, configBundleProductsDetails,
  checkAvailabilityData, selectableVariantsItemIds, accessoryItemIds, enableRemoveItemPromoImage, deliveryZip, storeZip
) => {
  const experienceType = configExperienceType || getExperienceType(bundleProduct);
  const bundleType = configBundleType || getBundleType(bundleProduct);
  const hasMissingProducts = isMissingProducts(bundleProductIds, bundleProducts,
    selectableVariantsItemIds, accessoryItemIds);

  const updatedProducts = bundleProducts?.map((product) => {
    const filteredProduct = bundleProduct?.productComponent?.find((component) =>
      component?.defaultProductId === product?.itemId);
    const isDiscontinued = isProductDiscontinued({ product, checkAvailabilityData });
    const quantity = Number(configBundleProductsDetails?.[product?.itemId]?.quantity) || filteredProduct?.quantity || 1;

    const excludedShipState = getExcludedShipState({
      bundleProductItem: product,
      deliveryZip,
      storeZip
    });
    return {
      ...product,
      media: getUpdatedMedia({
        product,
        removePromoImage: enableRemoveItemPromoImage && bundleProduct?.features?.removeItemPromoImage
      }),
      isIncomplete: hasMissingProducts,
      isDiscontinued,
      isOOS: isProductOOS({
        product: { ...product, quantity },
        experienceType,
        bundleType,
        checkAvailabilityData,
        isExcludedShipState: excludedShipState?.isExcluded
      }),
      excludedShipState,
      features: getProductsFeature(filteredProduct, bundleType, experienceType),
      quantity,
      priority: filteredProduct?.priority ? Number(filteredProduct.priority) : 0,
      isSelectableVariant: isSelectableVariantProduct({ product, selectableVariantsItemIds }),
      isAccessory: isAccessoryProduct({ product, accessoryItemIds })
    };
  });

  const sortedProducts = updatedProducts.sort((product1, product2) => product1.priority - product2.priority);
  return sortedProducts;
};

export const getItemIdsFromBundleComponents = (productIds, bundleComponentItemIds, configBundleProducts) => {
  return productIds || configBundleProducts || bundleComponentItemIds || [];
};

export const getBundleAccessoriesFromBundleProduct = (bundleProduct, enableAccessories) => {
  if (!enableAccessories) return [];
  const accessories = bundleProduct?.bundleSpecificationDetails?.accessories;

  if (!hasArrayValues(accessories?.categories)) {
    return [];
  }

  return accessories?.categories;
};

export const getItemIdsFromBundleAccessories = (bundleAccessories) => {
  const itemIds = [];

  (bundleAccessories || []).forEach((category) => {
    (category.offerings || []).forEach((offering) => {
      (offering.products || []).forEach((product) => {
        if (product.defaultProductId) {
          itemIds.push(product.defaultProductId);
        }
      });
    });
  });

  return [...new Set(itemIds)];
};

const normalizeOfferingName = (offeringName) => {
  if (!offeringName) return '';
  return offeringName.toLowerCase().trim();
};

const findOfferingConfig = (text) => {
  const matchedConfigs = OFFERING_CONFIGS.filter((config) => {
    return config.keywords?.every((keyword) => text.includes(keyword));
  });

  if (matchedConfigs.length === 1) {
    return matchedConfigs[0];
  }

  // select config based on its priority if multiple matches found
  const sortedConfigs = matchedConfigs.sort((configA, configB) => configB.priority - configA.priority);
  return sortedConfigs[0];
};

export const getOfferingDescription = (offeringName) => {
  const normalized = normalizeOfferingName(offeringName);
  return findOfferingConfig(normalized)?.description || '';
};

const sortOfferings = (offerings) => {
  return [...offerings].sort((offeringA, offeringB) => {
    const normalizedA = normalizeOfferingName(offeringA.offeringName);
    const normalizedB = normalizeOfferingName(offeringB.offeringName);

    const orderA = findOfferingConfig(normalizedA)?.priority;
    const orderB = findOfferingConfig(normalizedB)?.priority;
    if (orderA === Infinity && orderB === Infinity) {
      return 0;
    }
    return orderA - orderB;
  });
};

export const getUpdatedAccessoriesProduct = (
  accessoryProducts, bundleProduct, bundleAccessories, configExperienceType, configBundleType
) => {
  const experienceType = configExperienceType || getExperienceType(bundleProduct);
  const bundleType = configBundleType || getBundleType(bundleProduct);

  const sortByPriority = (_a, _b) => (_a.priority || 0) - (_b.priority || 0);

  const updateProduct = (baseProduct) => {
    const accessoryProduct = accessoryProducts?.find((accessory) => accessory.itemId === baseProduct.defaultProductId);
    const enrichedProduct = { ...accessoryProduct, ...baseProduct };

    return {
      ...enrichedProduct,
      features: getProductsFeature(enrichedProduct, bundleType, experienceType)
    };
  };

  const updateOffering = (offering) => ({
    ...offering,
    description: getOfferingDescription(offering.offeringName),
    products: offering.products?.map(updateProduct)?.sort(sortByPriority) || []
  });

  const updateCategory = (category) => ({
    ...category,
    offerings: sortOfferings(category.offerings?.map(updateOffering) || [])
  });

  return bundleAccessories?.map(updateCategory) || [];
};

const getMainBundleProducts = ({ bundleProducts }) => {
  return bundleProducts?.filter((product) => !product?.isSelectableVariant && !product?.isAccessory);
};

const getSelectableVariantProducts = ({ bundleProducts }) => {
  return bundleProducts?.filter((product) => product?.isSelectableVariant);
};

const getAccessoriesProducts = ({ bundleProducts }) => {
  return bundleProducts?.filter((product) => product?.isAccessory);
};

export const separateProductsByType = (products = []) => {
  return { updatedProducts: getMainBundleProducts({ bundleProducts: products }) || [],
    accessoryProducts: getAccessoriesProducts({ bundleProducts: products }) || [],
    selectableVariants: getSelectableVariantProducts({ bundleProducts: products }) || [] };
};
